import { Suspense, type ReactNode } from 'react';
import { useSelector } from 'react-redux';
import Media from 'react-media';
import loadable from '@loadable/component';
import { useTheme, type Theme } from '@simplywallst/ui-core';
import { cn } from '@simplywallst/ui-core/next';
import { getUi } from '@/redux/ui/selectors';
import { useSnackbar } from '@/features/snackbar/context';
import SwitcherContainer from '@/containers/SwitcherContainer';
import { BrazeProvider } from '@/components/Braze';
import { LayoutProvider } from '@/components/Layout/context/LayoutContext';
import { useUser } from '@/hooks/useUser';
import { useUserAgent } from '@simplywallst/hooks/useUserAgent';
import { useAppInit } from './hooks/useAppInit';
import { MainContainer } from './components/MainContainer';
import { AuthChecker } from '@/components/AuthChecker';
import { SimplyWallStSchema } from '@/components/DocumentSchema';
import { ModalManagementProvider } from '@/components/ModalManagement';
import { NarrativeWatchCompanyProvider } from '@/pages/Narrative/components/NarrativeWatchCompany';
import { useTracking } from 'react-tracking';
import { useApril2025Promo } from '@/hooks/useApril2025Promo';
import { useGetPlanTypeFromStorage } from '@/hooks/usePersistPlanType';
import { CountdownTimer } from './components/CountdownTimer/CountdownTimer';

const Snackbar = loadable(() => import('@/features/snackbar'), {
  resolveComponent: (c) => c.Snackbar,
  ssr: false,
});

const LoadingOverlay = loadable(() => import('@/components/LoadingOverlay'), {
  ssr: false,
});

const MobileFooterMenu = loadable(
  () => import('@/components/Nav/MobileFooterMenu')
);

const TrialEndedPlansModal = loadable(
  () =>
    import(
      /* webpackPrefetch: true */ '@/components/OurPlans/components/TrialEndedPlansModal/TrialEndedPlansModal'
    )
);

const UpgradePlansModal = loadable(
  () =>
    import(
      /* webpackPrefetch: true */ '@/components/OurPlans/components/UpgradePlansModal/UpgradePlansModal'
    )
);

const Onboarding = loadable(
  () => import(/* webpackPrefetch: true */ '@/components/Onboarding'),
  {
    resolveComponent: (c) => c.Onboarding,
  }
);

const SubscriptionRecovery = loadable(
  () => import('@/components/SubscriptionRecovery'),
  { resolveComponent: (c) => c.SubscriptionRecovery }
);

const CheckoutReturnModal = loadable(
  () => import('@/components/OurPlans/components/CheckoutReturnModal')
);

const AppUpdate = loadable(() => import('@/components/AppUpdate'), {
  ssr: false,
  resolveComponent: (component) => component.AppUpdate,
});

const AppUrlListener = loadable(() => import('@/components/AppUrlListener'), {
  resolveComponent: (component) => component.AppUrlListener,
});

const NativeAppInit = loadable(() => import('../NativeAppInit/NativeAppInit'), {
  resolveComponent: (component) => component.NativeAppInit,
});

export interface WithThemeProps {
  theme: Theme;
}

export interface Props {
  layout: Record<string, unknown>;
  children?: ReactNode;
}

const Banner = () => {
  const { trackEvent } = useTracking({ type: 'track' });
  const { data: user } = useUser();
  const { isPromoActive, variant } = useApril2025Promo();
  const { guestPlanType } = useGetPlanTypeFromStorage();

  if (!isPromoActive) return;

  const bannerLabel = {
    default:
      user.loggedIn ||
      ['unlimited', 'premium', 'free', 'advisor'].includes(guestPlanType)
        ? 'Easter Sale'
        : 'Introductory Sale',
    endingSoon: 'Ending Soon',
    lastChance: 'Last Chance',
    countdown: 'Ends in ...',
  } as const;

  type BannerVariant = keyof typeof bannerLabel;
  const bannerVariant = (variant ?? 'default') as BannerVariant;

  const label = bannerLabel[bannerVariant] ?? bannerLabel.default;

  const promoInfo = {
    subject: 'bannerPromo',
    discountAmount: 0.4,
    bannerDetail: label,
    couponId: 'MaCR3VwK',
  };

  return (
    <a
      href={`https://simplywall.st/plans?coupon=${promoInfo.couponId}`}
      className="relative block h-10 select-none justify-items-center bg-[#f4f2ec] bg-[url('https://cdn.prod.website-files.com/648009cb318551f1806b36b4/67f753e4fa47b33e78eda2f1_easter-promo-bg-2.png')] bg-center px-x2 text-sm text-[#006d50] no-underline md:h-12 md:text-[18px] lg:z-1 lg:px-x3"
      onClick={() => {
        trackEvent({ action: 'click', ...promoInfo });
      }}
      target="_blank"
      rel="noreferrer"
    >
      <div className="mx-auto flex h-full max-w-[1158px] items-center justify-center gap-2 whitespace-nowrap">
        <div>
          <span className="font-extrabold">40% Off</span>&nbsp;All Plans
        </div>
        {bannerVariant === 'countdown' ? (
          <CountdownTimer />
        ) : (
          <div className="rounded-full bg-[#e0eee1] px-2 py-0.5 text-[12px] max-[320px]:hidden md:text-[14px]">
            {promoInfo.bannerDetail}
          </div>
        )}
      </div>
    </a>
  );
};

const Content = ({ layout, children }: Props) => {
  const theme = useTheme();
  const ui = useSelector(getUi);
  const snackbar = useSnackbar();
  const { data: user } = useUser();
  const { isMobile, isTablet } = useUserAgent();

  const isLoading = ui.loading;
  const hasSnackbar = snackbar.state.keys.length > 0;

  useAppInit();

  return (
    <MainContainer>
      <BrazeProvider>
        <ModalManagementProvider>
          <LayoutProvider layout={layout}>
            <NarrativeWatchCompanyProvider>
              <Banner />
              <div
                className={cn(
                  'relative z-0 flex flex-wrap justify-center',
                  layout &&
                    layout.mobileFooterMenu !== false &&
                    'max-lg:pb-[calc(theme(spacing.x8)+env(safe-area-inset-bottom))]'
                )}
              >
                <AuthChecker />
                <SimplyWallStSchema />
                <Suspense
                  fallback={
                    <div className="p-5 text-sm text-legacy-white">
                      Loading...
                    </div>
                  }
                >
                  {children ?? <SwitcherContainer />}
                </Suspense>
              </div>
              {layout && layout.mobileFooterMenu !== false && (
                <Media
                  query={{ maxWidth: theme.mdMaxRaw }}
                  defaultMatches={isMobile || isTablet}
                  render={() => <MobileFooterMenu />}
                />
              )}
              {layout && (
                <>
                  {hasSnackbar && <Snackbar />}
                  {isLoading && <LoadingOverlay />}

                  <TrialEndedPlansModal />
                  <SubscriptionRecovery />
                  {user.loggedIn && <CheckoutReturnModal />}
                  {user.loggedIn && <UpgradePlansModal />}
                  <Onboarding />
                </>
              )}
              {RUNTIME_ENV === 'native' && user.loggedIn && <AppUpdate />}
              {RUNTIME_ENV === 'native' && <AppUrlListener />}
              {RUNTIME_ENV === 'native' && <NativeAppInit />}
            </NarrativeWatchCompanyProvider>
          </LayoutProvider>
        </ModalManagementProvider>
      </BrazeProvider>
    </MainContainer>
  );
};

export default Content;
